import { PageHeader } from "../../UIPalette/components/PageHeader/PageHeader";
import { useTranslation } from "react-i18next";
import { RowMenuItem, Table, TableHeader } from "../../UIPalette/components/Table/Table";
import { OrderByDirection, RoleKeys } from "core/Constants";
import { useState } from "react";
import { useDeleteNotificationFunctionRequest, useListPendingPushNotificationFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { useDebounce } from "shared/core/hooks/useDebounce";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { useNavigate } from "react-router-dom";
import Button from "shared/UI/Buttons/Button";
import "shared/extensions/string";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import { Input } from "shared/UI/Input/Input";
import { ReactComponent as SearchIcon } from "@icons/magnifying-glass.svg";
import { Select } from "shared/UI/Select/Select";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import InputDatePicker from "shared/UI/DatePicker/InputDatePicker/InputDatePicker";

export enum ListNotificationsOrderBy {
  Title,
  Body,
  LinkUrl,
  PublishDate,
  CreatedDateUtc,
}

export enum FilterDateBy {
  CreatedDate,
  PublishDate,
}

export const PendingNotifications = () => {
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const navigateHook = useNavigate();
  const { trigger: deleteNotificationFunctionRequest } = useDeleteNotificationFunctionRequest();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [ordering, setOrdering] = useState({
    column: ListNotificationsOrderBy.CreatedDateUtc,
    direction: OrderByDirection.Descending,
  });
  const [paging, setPaging] = useState({ current: 1, size: 10 });
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNotificationIndex, setSelectedNotificationIndex] = useState(0);

  const [filters, setFilters] = useState<{ dateFrom?: Date; dateTo?: Date; filterDateBy?: string }>();

  const { isAuthorised } = useAdminUserContext();
  const {
    data: pushNotifications,
    error,
    mutate,
  } = useListPendingPushNotificationFunctionRequest({
    from: filters?.dateFrom?.toDateOnlyString(),
    to: filters?.dateTo?.toDateOnlyString(),
    dateFilterBy: Number(filters?.filterDateBy ?? ""),
    keyword: debouncedSearchKeyword,
    page: paging.current,
    perPage: paging.size,
    orderBy: ordering.column,
    orderByDirection: ordering.direction,
    isPending: true,
  });

  const tableHeaders: TableHeader[] = [
    { text: t("pendingNotifications.table.titleHeading"), orderByValue: ListNotificationsOrderBy.Title },
    { text: t("pendingNotifications.table.bodyHeading"), orderByValue: ListNotificationsOrderBy.Body },
    {
      text: t("pendingNotifications.table.linkHeading"),
      orderByValue: ListNotificationsOrderBy.LinkUrl,
    },
    {
      text: t("pendingNotifications.table.publishDateHeading"),
      orderByValue: ListNotificationsOrderBy.PublishDate,
    },
    {
      text: t("pendingNotifications.table.createdDateHeading"),
      orderByValue: ListNotificationsOrderBy.CreatedDateUtc,
    },
  ];

  const mapTableData = () =>
    pushNotifications?.notifications?.map(({ title, body, publishDateUtc, linkUrl, createdDateUtc }) => {
      return {
        title,
        body,
        linkUrl,
        publishDateUtc: String.formatDateTime(publishDateUtc),
        createdDateUtc: String.formatDateTime(createdDateUtc),
      };
    }) ?? [];

  const onDeleteNotification = async (rowIndex: number) => {
    setSelectedNotificationIndex(rowIndex);
    setShowDeleteModal(true);
  };

  const buildRowMenuItems = () => {
    let rowMenuItems: RowMenuItem[] = [];

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["send-push-notifications"] })) {
      rowMenuItems.push({
        title: t("table.deleteTitle"),
        "aria-label": t("table.ariaDeleteRow"),
        onClick: (rowIndex: number) => onDeleteNotification(rowIndex),
      });
    }

    return rowMenuItems;
  };

  const deleteNotification = async () => {
    try {
      const NotificationId = pushNotifications!.notifications![selectedNotificationIndex]?.notificationId!;
      await deleteNotificationFunctionRequest({ query: { NotificationId } });
      await mutate();
      modalNotify("success", t("pendingNotifications.notificationDelete.successTitle"), t("pendingNotifications.notificationDelete.successMessage"));
    } catch {
      modalNotify(
        "error",
        t("pendingNotifications.notificationDelete.requestErrorTitle"),
        t("pendingNotifications.notificationDelete.requestErrorMessage"),
        t("pendingNotifications.notificationDelete.requestErrorCta")
      );
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <PageHeader title={t("pendingNotifications.pageTitle")} subtitle={t("pendingNotifications.pageSubHeading")} />
      <div className="w-2/3 mt-2 mb-6 content-center">
        <Input
          aria-label={t("pendingNotifications.keywordLabel")}
          onChange={(a) => {
            setPaging({ ...paging, current: 1 });
            setSearchKeyword(a.target.value);
          }}
          icon={<SearchIcon />}
          placeholder={t("pendingNotifications.keywordPlaceholder")}
          label={t("pendingNotifications.keywordLabel")}
        />
        <div className="flex flex-row mb-3.5 space-x-3.5">
          <div className="relative basis-1/2">
            <Select
              onChange={(e) => setFilters((prev) => ({ ...prev, filterDateBy: String(e.target.value) }))}
              aria-label={t("pendingNotifications.dateFilterLabel")}
              value={filters?.filterDateBy}
              label={t("pendingNotifications.dateFilterLabel")}
              placeholder={t("pendingNotifications.dateFilterLabel")}
            >
              <option value={FilterDateBy.CreatedDate}>{t("pendingNotifications.createdDateTitle")}</option>
              <option value={FilterDateBy.PublishDate}>{t("pendingNotifications.publishedDateTitle")}</option>
            </Select>
          </div>
          <div className="relative basis-1/2">
            <InputDatePicker
              label={t("pendingNotifications.fromDateLabel")}
              disabled={!filters?.filterDateBy}
              date={filters?.dateFrom}
              setDate={(date) => setFilters((prev) => ({ ...prev, dateFrom: date }))}
              {...{
                className: "[&>input]:bg-l-blue border-r-4 border-none [&>svg]:z-10 cursor-pointer leading-8",
                name: "dateFrom",
              }}
            />
          </div>
          <div className="relative basis-1/2">
            <InputDatePicker
              label={t("pendingNotifications.toDateLabel")}
              disabled={!filters?.filterDateBy}
              date={filters?.dateTo}
              setDate={(date) => setFilters((prev) => ({ ...prev, dateTo: date }))}
              {...{
                className: "[&>input]:bg-l-blue border-r-4 border-none [&>svg]:z-10 cursor-pointer leading-8",
                name: "dateTo",
              }}
              minDate={filters?.dateFrom}
            />
          </div>
        </div>
      </div>

      <Table
        headers={tableHeaders}
        dataRows={mapTableData()}
        orderedByColumn={ordering.column}
        orderedDirection={ordering.direction}
        currentPage={paging.current}
        pageSize={paging.size}
        totalDataCount={pushNotifications?.metadata?.totalCount ?? 0}
        onOrderingChange={setOrdering}
        onPagingChange={setPaging}
        actionButtons={<Button text={"+"} onClick={() => navigateHook("/communications")} />}
        rowMenuItems={buildRowMenuItems()}
      />
      {showDeleteModal && (
        <ConfirmationModal
          title={t("pendingNotifications.notificationDelete.modalTitle")}
          message={t("pendingNotifications.notificationDelete.modalMessage", {
            title: pushNotifications!.notifications![selectedNotificationIndex]?.title ?? "",
          })}
          cancelButtonText={t("pendingNotifications.notificationDelete.modalDismissTitle")}
          confirmButtonText={t("pendingNotifications.notificationDelete.modalConfirmTitle")}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={deleteNotification}
        />
      )}
      {error && <ErrorAlert content={t("pendingNotifications.requestErrorMessage")} />}
    </>
  );
};
